import type React from 'react';
import { useTranslation } from 'react-i18next';
import { Row, Col, InputNumber, Typography, Form } from 'antd';
import { stringReplaceAll } from 'utils/format';
import {
  FireModifierResponseDto,
  GetTailoredResultQueryDtoPriceRatingTypeEnum,
  GetTailoredResultQueryDtoTailoredResultTypeEnum,
  SubmissionResponseDto,
} from 'libs/pinnacle-sdk';
import { EQueryKeys } from 'constants/query-keys';
import { useQuery } from 'react-query';
import { priceRatingApi } from 'services/apis';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { ESocketEvents } from 'hooks/useWebSocket';
import debounce from 'lodash/debounce';
import { isSubmissionEditable } from 'utils/submission';
import TextArea from 'antd/es/input/TextArea';
import { isEmpty } from 'lodash';
import FormActionButtons from 'components/ui/form-action-buttons';

const { Title, Text } = Typography;

interface ModifierInputProps {
  label: string;
  value: string;
  onChange?: (value: number | null) => void;
  disabled?: boolean;
  isTotal?: boolean;
}

const ModifierInput: React.FC<ModifierInputProps> = ({
  label,
  value,
  onChange,
  disabled = false,
  isTotal = false,
}) => {
  return (
    <div>
      <Text strong className="block mb-2">
        {label}
      </Text>
      <InputNumber
        className={`w-full rounded-lg h-10 flex items-center ${
          isTotal ? 'bg-gray-100' : ''
        }`}
        value={parseFloat(value || '0')}
        onChange={onChange}
        disabled={disabled}
        formatter={(value) => `${value}%`}
        parser={(value) =>
          parseFloat(value ? stringReplaceAll(value, '%', '') : '0')
        }
        step={0.01}
        precision={2}
        controls={false}
      />
    </div>
  );
};

const FireModifiers: React.FC<{
  submission: SubmissionResponseDto;
  sendMessage: (event: string, data?: unknown) => void;
}> = ({ submission, sendMessage }) => {
  const { t } = useTranslation();

  const isEditingMode = useMemo(() => {
    return isSubmissionEditable(submission.status);
  }, [submission.status]);

  const { data: fireModifiers } = useQuery<FireModifierResponseDto>(
    [EQueryKeys.FIRE_MODIFIERS, submission.submissionId],
    () => priceRatingApi.getFireModifiers(submission.submissionId)
  );

  // The reason we need to use a local state is because the fireModifiers
  // is not updated immediately when the user changes the value in BE
  // we need to use a local state to store the value
  // so that the modified values are not conflict with the value in BE
  const [localModifiers, setLocalModifiers] = useState<Record<string, string>>(
    {}
  );
  const [localComment, setLocalComment] = useState<string>('');

  const modifierCategories = useMemo(
    () => [
      {
        key: 'construction' as const,
        label: t('fireModifiers.construction'),
      },
      {
        key: 'yearBuilt' as const,
        label: t('fireModifiers.yearBuilt'),
      },
      {
        key: 'fireProtection' as const,
        label: t('fireModifiers.fireProtection'),
      },
      {
        key: 'riskManagement' as const,
        label: t('fireModifiers.riskManagement'),
      },
      {
        key: 'coverageDeductibles' as const,
        label: t('fireModifiers.coverageDeductibles'),
      },
    ],
    [t]
  );

  useEffect(() => {
    if (fireModifiers && Object.keys(localModifiers).length === 0) {
      const initialValues: Record<string, string> = {};
      modifierCategories.forEach((category) => {
        initialValues[category.key] = fireModifiers[category.key] || '0';
      });
      setLocalModifiers(initialValues);
    }
  }, [fireModifiers, modifierCategories, localModifiers]);

  useEffect(() => {
    if (fireModifiers?.comment && !isEmpty(fireModifiers.comment)) {
      setLocalComment(fireModifiers.comment);
    }
  }, [fireModifiers?.comment]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedSend = useCallback(
    debounce((messageData: Record<string, number | string>) => {
      sendMessage(ESocketEvents.ON_DATA_CHANGE, {
        submissionId: submission.submissionId,
        tailoredResultType:
          GetTailoredResultQueryDtoTailoredResultTypeEnum.FireModifiers,
        priceRatingType:
          GetTailoredResultQueryDtoPriceRatingTypeEnum.FireRating,
        data: messageData,
      });
    }, 500),
    [submission.submissionId, sendMessage]
  );

  const handleModifierChange = useCallback(
    (value: number | null, key: keyof FireModifierResponseDto) => {
      // Only allow changes if editing mode is enabled
      if (!isEditingMode) {
        return;
      }

      if (value === null) return;

      setLocalModifiers((prev) => ({
        ...prev,
        [key]: value.toString(),
      }));

      const messageData = modifierCategories.reduce(
        (acc, category) => {
          acc[category.key] = parseFloat(
            category.key === key
              ? value.toString()
              : localModifiers[category.key] || '0'
          );
          return acc;
        },
        {} as Record<string, number>
      );
      debouncedSend(messageData);
    },
    [localModifiers, debouncedSend, modifierCategories, isEditingMode]
  );

  return (
    <div className="rounded-lg bg-white px-3 py-4">
      <Title level={4} className="mb-6 ml-4">
        {t('fireModifiers.fireModifiers')}
      </Title>

      <Row gutter={16}>
        {modifierCategories.map((category) => (
          <Col key={category.key} xs={24} sm={12} md={8} lg={4}>
            <ModifierInput
              label={category.label}
              value={localModifiers[category.key] || '0'}
              onChange={(value) => handleModifierChange(value, category.key)}
              disabled={!isEditingMode}
            />
          </Col>
        ))}

        <Col xs={24} sm={12} md={8} lg={4}>
          <ModifierInput
            label={t('fireModifiers.totalFireModifier')}
            value={fireModifiers?.totalFireModifier || '0'}
            disabled={true}
            isTotal={true}
          />
        </Col>
      </Row>

      <Text strong className="block mb-2 mt-4">
        {t('general.comments')}
      </Text>

      <TextArea
        rows={4}
        disabled={!isEditingMode}
        placeholder={t('general.enter', {
          field: t('general.comments'),
        })}
        value={localComment}
        onChange={(e) => {
          const newComment = e.target.value;
          setLocalComment(newComment);
        }}
      />

      <FormActionButtons
        containerClassName="mt-4"
        confirmText={t('general.save')}
        isConfirmDisabled={false}
        includeCancel={false}
        includeConfirm={isEditingMode}
        confirmOutlined={false}
        onConfirm={() => {
          debouncedSend({
            comment: localComment,
          });
        }}
      />
    </div>
  );
};

export default FireModifiers;
